/* global google */

import React, { useState, useEffect, useRef } from 'react';

import { Map, View, Feature, Overlay } from 'ol';
import { fromLonLat } from 'ol/proj';

import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';

import VectorSource from 'ol/source/Vector';
import XYZ from 'ol/source/XYZ';
import OSM from 'ol/source/OSM';

import Point from 'ol/geom/Point';
import LineString from 'ol/geom/LineString';

import Style from 'ol/style/Style';
import Icon from 'ol/style/Icon';
import Stroke from 'ol/style/Stroke';
import Fill from 'ol/style/Fill';
import Text from 'ol/style/Text';

import { defaults as defaultControls, Zoom } from 'ol/control';

import haversine from 'haversine';
import lodash from 'lodash';

import {
  Box,
  ToggleButton,
  Tooltip,
  Alert,
} from '@mui/material';

import LocationSearchingIcon from '@mui/icons-material/LocationSearching';
import SatelliteAltIcon from '@mui/icons-material/SatelliteAlt';
import PublicIcon from '@mui/icons-material/Public';

import { useTheme } from '@mui/material/styles';

import busIcon from '../../../assets/marker_bus.png';
import stopIcon from '../../../assets/marker_stop.png';

import {
  startThingUpdatesNoJourney,
  stopUpdates,
} from './LiveApis';

import 'ol/ol.css';

function Openlayer(props) {
  const mapElement = useRef(null);

  const [map, setMap] = useState(null);

  const [follow, setFollow] = useState(false);
  const followRef = useRef(false);

  useEffect(() => {
  followRef.current = follow;
}, [follow]);

  const [satelliteView, setSatelliteView] =
    useState(false);

  const [sessionValid, setSessionValid] =
    useState(true);

  const theme = useTheme();

  const liveiconSource = useRef(
    new VectorSource()
  ).current;

  const stopSource = useRef(
    new VectorSource()
  ).current;

  const pathCoveredSource = useRef(
    new VectorSource()
  ).current;

  const pathRemainSource = useRef(
    new VectorSource()
  ).current;

  const view = useRef(
    new View({
      zoom: 15,
      maxZoom: 20,
    })
  ).current;

  const vehicleFeatureRef = useRef(null);

  const animationRef = useRef(null);

  const flag = useRef(1);

  const isFirstLocation = useRef(true);

  // =========================================================
  // MAP INIT
  // =========================================================

  useEffect(() => {
    if (!mapElement.current) return;

    const osmLayer = new TileLayer({
      visible: !satelliteView,
      source: new OSM(),
    });

    const satelliteLayer = new TileLayer({
      visible: satelliteView,
      source: new XYZ({
        url: 'https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
      }),
    });

    const liveLayer = new VectorLayer({
      source: liveiconSource,
      zIndex: 20,
    });

    const stopLayer = new VectorLayer({
      source: stopSource,
      zIndex: 15,
    });

    const pathCoveredLayer = new VectorLayer({
      source: pathCoveredSource,
      zIndex: 10,
    });

    const pathRemainLayer = new VectorLayer({
      source: pathRemainSource,
      zIndex: 11,
    });

    const initialMap = new Map({
      target: mapElement.current,

      layers: [
        osmLayer,
        satelliteLayer,
        pathCoveredLayer,
        pathRemainLayer,
        stopLayer,
        liveLayer,
      ],

      view,

      controls: defaultControls({
        attribution: false,
        zoom: false,
      }).extend([
        new Zoom({
          className: 'mui-zoom-control',
        }),
      ]),
    });

    // zoom styling

    setTimeout(() => {
      const el = document.querySelector(
        '.mui-zoom-control'
      );

      if (!el) return;

      Object.assign(el.style, {
        position: 'absolute',
        bottom: '20px',
        right: '20px',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '12px',
        overflow: 'hidden',
        zIndex: 1000,
        background:
          theme.palette.background.paper,
        boxShadow:
          '0 3px 8px rgba(0,0,0,0.25)',
      });
    }, 300);

    // popup

    const container =
      document.createElement('div');

    container.className = 'ol-popup';

    const closer =
      document.createElement('a');

    closer.className =
      'ol-popup-closer';

    closer.href = '#';

    const content =
      document.createElement('div');

    container.appendChild(closer);

    container.appendChild(content);

    document.body.appendChild(container);

    const popup = new Overlay({
      element: container,
      autoPan: true,
    });

    initialMap.addOverlay(popup);

    closer.onclick = () => {
      popup.setPosition(undefined);
      return false;
    };

    initialMap.on('click', (evt) => {
      initialMap.forEachFeatureAtPixel(
        evt.pixel,
        (feature) => {
          const props =
            feature.getProperties();

          if (props.type === 'stop') {
            content.innerHTML = `
              <div style="padding:8px;font-weight:600;">
                ${props.name}
              </div>
            `;

            popup.setPosition(
              feature
                .getGeometry()
                .getCoordinates()
            );
          }
        }
      );
    });

    setMap(initialMap);

    return () => {
      stopUpdates();

      if (animationRef.current) {
        cancelAnimationFrame(
          animationRef.current
        );
      }

      initialMap.setTarget(undefined);

      if (container.parentNode) {
        container.parentNode.removeChild(
          container
        );
      }
    };
  }, []);

  // =========================================================
  // START LIVE
  // =========================================================

  useEffect(() => {
    if (!map) return;

    startUpdate();

    getDetails();

    const interval = setInterval(() => {
      getDetails();
    }, 60000);

    return () => clearInterval(interval);
  }, [map]);

  // =========================================================
  // LIVE SOCKET
  // =========================================================

  const startUpdate = () => {
    startThingUpdatesNoJourney((data) => {
      const latitude =
        data?.location?.latitude ||
        data?.latitude;

      const longitude =
        data?.location?.longitude ||
        data?.longitude;

      props.updateDeviceStatus?.(
        data?.message ||
          'Device Connected'
      );

      if (
        latitude !== undefined &&
        longitude !== undefined
      ) {
        currentIcon([
          longitude,
          latitude,
        ]);
      }
    });
  };

  // =========================================================
  // SESSION CHECK
  // =========================================================

  const getDetails = async () => {
    if (flag.current === 0) return;

    flag.current = 0;

    try {
      const response = await fetch(
        `https://v3.dhundhoo.com/tempUrl/get?thingId=${props.authParams?.thingId}`,
        {
          method: 'GET',

          headers: {
            'x-dhundhoo-session':
              localStorage.getItem(
                'dhundhoo-sessioncode'
              ) ||
              localStorage.getItem(
                'token'
              ),
          },
        }
      );

      const rawData =
        await response.text();

      let data;

      try {
        data = JSON.parse(rawData);
      } catch (e) {
        flag.current = 1;
        return;
      }

      // =====================================================
      // INVALID
      // =====================================================

      if (
        data?.status === false ||
        data?.status === 'false' ||
        data?.message?.includes(
          'API Key is not found'
        ) ||
        data?.message?.includes(
          'link is expired'
        )
      ) {
        stopUpdates();

        liveiconSource.clear();

        setSessionValid(false);

        return;
      }

      // =====================================================
      // VALID
      // =====================================================

      setSessionValid(true);

      // initial load only

      if (isFirstLocation.current) {
        try {
          const trackResponse =
            await fetch(
              `https://v3.dhundhoo.com/vendor/thing/thingtrack?thingId=${props.authParams?.thingId}`
            );

          const trackData =
            await trackResponse.json();

          const latitude =
            trackData?.location
              ?.latitude ||
            trackData?.latitude;

          const longitude =
            trackData?.location
              ?.longitude ||
            trackData?.longitude;

          if (
            latitude !== undefined &&
            longitude !== undefined
          ) {
            currentIcon([
              longitude,
              latitude,
            ]);
          }
        } catch (err) {
          console.error(err);
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      flag.current = 1;
    }
  };

  // =========================================================
  // SMOOTH VEHICLE MOVEMENT
  // =========================================================

  const currentIcon = (location) => {
    if (
      !location ||
      location.some(isNaN)
    )
      return;

    const newCoord =
      fromLonLat(location);

    // =====================================================
    // FIRST TIME
    // =====================================================

    if (!vehicleFeatureRef.current) {
      const feature = new Feature({
        geometry: new Point(
          newCoord
        ),
      });

      feature.setStyle(
        new Style({
          image: new Icon({
            src: busIcon,
            scale: 0.4,
            anchor: [0.5, 1],
          }),
        })
      );

      vehicleFeatureRef.current =
        feature;

      liveiconSource.addFeature(
        feature
      );

      // only first time map move
      view.setCenter(newCoord);

      view.setZoom(17);

      isFirstLocation.current =
        false;

      return;
    }

    // =====================================================
    // ANIMATE MARKER ONLY
    // =====================================================

    const geometry =
      vehicleFeatureRef.current.getGeometry();

    const oldCoord =
      geometry.getCoordinates();

    if (animationRef.current) {
      cancelAnimationFrame(
        animationRef.current
      );
    }

    const duration = 1500;

    const start =
      performance.now();

    const animateMarker = (now) => {
  const elapsed = now - start;
  const progress = Math.min(elapsed / duration, 1);

  const ease =
    progress < 0.5
      ? 2 * progress * progress
      : 1 - Math.pow(-2 * progress + 2, 2) / 2;

  const currentX =
    oldCoord[0] + (newCoord[0] - oldCoord[0]) * ease;

  const currentY =
    oldCoord[1] + (newCoord[1] - oldCoord[1]) * ease;

  const currentPosition = [currentX, currentY];

  geometry.setCoordinates(currentPosition);

  // ✅ ab ye hamesha latest follow value dekhega
  if (followRef.current) {
    view.setCenter(currentPosition);
  }

  if (progress < 1) {
    animationRef.current = requestAnimationFrame(animateMarker);
  }
};

    animationRef.current =
      requestAnimationFrame(
        animateMarker
      );
  };

  // =========================================================
  // ROUTE + STOPS
  // =========================================================

  useEffect(() => {
    if (
      !props.thingModel
        ?.currentJourney ||
      !props.position
    ) {
      pathCoveredSource.clear();

      pathRemainSource.clear();

      stopSource.clear();

      return;
    }

    const model =
      props.thingModel;

    // ================= STOPS =================

    stopSource.clear();

    const stops =
      model.currentJourney.stops || [];

    stops.forEach((stop, i) => {
      const coord = fromLonLat([
        stop.location.longitude,
        stop.location.latitude,
      ]);

      const feat = new Feature({
        geometry: new Point(coord),
      });

      feat.setStyle(
        new Style({
          image: new Icon({
            src: stopIcon,
            scale: 0.3,
            anchor: [0.5, 46],
            anchorYUnits:
              'pixels',
          }),

          text: new Text({
            text: `${i + 1}`,

            offsetY: -20,

            fill: new Fill({
              color: '#fff',
            }),

            backgroundFill:
              new Fill({
                color:
                  '#1976d2',
              }),

            padding: [
              2, 6, 2, 6,
            ],
          }),
        })
      );

      feat.setProperties({
        name: stop.locationName,
        type: 'stop',
      });

      stopSource.addFeature(feat);
    });

    // ================= ROUTE =================

    if (
      model.currentJourney
        .wayPointsPolyLine &&
      window.google
    ) {
      const path =
        google.maps.geometry.encoding.decodePath(
          model.currentJourney
            .wayPointsPolyLine
        );

      if (!lodash.isEmpty(path)) {
        let matchIndex = 0;

        let min = Infinity;

        path.forEach(
          (point, index) => {
            const distance =
              haversine(
                {
                  latitude:
                    point.lat(),
                  longitude:
                    point.lng(),
                },
                {
                  latitude:
                    props.position
                      .latitude,
                  longitude:
                    props.position
                      .longitude,
                },
                {
                  unit:
                    'meter',
                }
              );

            if (
              distance < min
            ) {
              min = distance;

              matchIndex =
                index;
            }
          }
        );

        const covered =
          path
            .slice(
              0,
              matchIndex
            )
            .map((p) => [
              p.lng(),
              p.lat(),
            ]);

        const remaining =
          path
            .slice(
              matchIndex
            )
            .map((p) => [
              p.lng(),
              p.lat(),
            ]);

        drawLine(
          covered,
          '#4CAF50',
          pathCoveredSource
        );

        drawLine(
          remaining,
          '#FF9800',
          pathRemainSource
        );
      }
    }
  }, [
    props.thingModel,
    props.position,
  ]);

  // =========================================================
  // DRAW LINE
  // =========================================================

  const drawLine = (
    coord,
    color,
    source
  ) => {
    source.clear();

    if (
      !coord ||
      coord.length < 2
    )
      return;

    const lineString =
      new LineString(coord);

    lineString.transform(
      'EPSG:4326',
      'EPSG:3857'
    );

    const feature = new Feature({
      geometry: lineString,
    });

    feature.setStyle(
      new Style({
        stroke: new Stroke({
          color,
          width: 4,
        }),
      })
    );

    source.addFeature(feature);
  };

  // =========================================================
  // SATELLITE
  // =========================================================

  const toggleSatelliteView =
    () => {
      const layers =
        map
          ?.getLayers()
          ?.getArray();

      if (!layers) return;

      const osmLayer =
        layers[0];

      const satelliteLayer =
        layers[1];

      if (satelliteView) {
        osmLayer.setVisible(
          true
        );

        satelliteLayer.setVisible(
          false
        );
      } else {
        osmLayer.setVisible(
          false
        );

        satelliteLayer.setVisible(
          true
        );
      }

      setSatelliteView(
        !satelliteView
      );
    };

  // =========================================================
  // INVALID UI
  // =========================================================

  if (sessionValid === false) {
    return (
      <Box
        sx={{
          height: '90vh',
          display: 'flex',
          justifyContent:
            'center',
          alignItems:
            'center',
          p: 2,
        }}
      >
        <Alert severity="error">
          API Key is not found or
          link is expired
        </Alert>
      </Box>
    );
  }

  // =========================================================
  // UI
  // =========================================================

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '90vh',
      }}
    >
      <Box
        ref={mapElement}
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      />

      {/* FOLLOW */}

      <Tooltip
        title={
          follow
            ? 'Following'
            : 'Follow vehicle'
        }
      >
        <ToggleButton
          value="follow"
          selected={follow}
          onChange={() =>
            setFollow(!follow)
          }
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 10,
            bgcolor:
              'background.paper',
            borderRadius: 2,
            width: 48,
            height: 48,
          }}
        >
          <LocationSearchingIcon />
        </ToggleButton>
      </Tooltip>

      {/* SATELLITE */}

      <Tooltip
        title={
          satelliteView
            ? 'Satellite View'
            : 'Street View'
        }
      >
        <ToggleButton
          value="satellite"
          selected={satelliteView}
          onChange={
            toggleSatelliteView
          }
          sx={{
            position: 'absolute',
            top: 72,
            right: 16,
            zIndex: 10,
            bgcolor:
              'background.paper',
            borderRadius: 2,
            width: 48,
            height: 48,
          }}
        >
          {satelliteView ? (
            <SatelliteAltIcon />
          ) : (
            <PublicIcon />
          )}
        </ToggleButton>
      </Tooltip>
    </Box>
  );
}

export default Openlayer;